import type { HttpClient } from '../http/HttpClient'
import type {
  InstallmentRef,
  InvoiceElsewhere,
  InvoiceMoveMode,
  InvoiceMovePlan,
  InvoiceMoveRepository,
  InvoiceMoveRequest,
  MoveInvoiceRef,
  PurchaseOrderChange,
  PurchaseOrderRef,
} from '@/domain/contract/invoiceMove'

interface RawInstallment {
  payment_id: number
  number: number
  status: string
  programmed_payment_date: string | null
  estimated_total: number
  total: number
  currency: string | null
  total_after?: number | null
}

interface RawInvoiceRef {
  id: number
  number: string
  total: number
}

interface RawPurchaseOrderChange {
  current: PurchaseOrderRef | null
  after: PurchaseOrderRef | null
  changes: boolean
}

interface RawPlan {
  invoice: RawInvoiceRef
  origin: RawInstallment
  destination: RawInstallment & { invoices: RawInvoiceRef[] }
  modes: { mode: InvoiceMoveMode; origin_total_after: number; destination_total_after: number }[]
  blocks: { code: string; message: string; payment_id: number | null; status?: string | null }[]
  purchase_order: RawPurchaseOrderChange
  swapped_invoice_purchase_order: RawPurchaseOrderChange | null
}

export interface RawInvoiceElsewhere {
  id: number
  number: string
  total: number
  currency: string | null
  emission_date: string
  payment: { payment_id: number; number: number; programmed_payment_date: string | null; status: string }
  can_move: boolean
}

export function toInvoiceElsewhere(raw: RawInvoiceElsewhere): InvoiceElsewhere {
  return {
    id: raw.id,
    number: raw.number,
    total: raw.total,
    currency: raw.currency,
    emissionDate: raw.emission_date,
    payment: {
      paymentId: raw.payment.payment_id,
      number: raw.payment.number,
      programmedPaymentDate: raw.payment.programmed_payment_date,
      status: raw.payment.status,
    },
    canMove: raw.can_move,
  }
}

function toInstallment(raw: RawInstallment): InstallmentRef {
  return {
    paymentId: raw.payment_id,
    number: raw.number,
    status: raw.status,
    programmedPaymentDate: raw.programmed_payment_date,
    estimatedTotal: raw.estimated_total,
    total: raw.total,
    currency: raw.currency,
    totalAfter: raw.total_after ?? null,
  }
}

function toInvoiceRef(raw: RawInvoiceRef): MoveInvoiceRef {
  return { id: raw.id, number: raw.number, total: raw.total }
}

function toPurchaseOrderChange(raw: RawPurchaseOrderChange): PurchaseOrderChange {
  return { current: raw.current, after: raw.after, changes: raw.changes }
}

function toPlan(raw: RawPlan): InvoiceMovePlan {
  return {
    invoice: toInvoiceRef(raw.invoice),
    origin: toInstallment(raw.origin),
    destination: { ...toInstallment(raw.destination), invoices: raw.destination.invoices.map(toInvoiceRef) },
    modes: raw.modes.map((outcome) => ({
      mode: outcome.mode,
      originTotalAfter: outcome.origin_total_after,
      destinationTotalAfter: outcome.destination_total_after,
    })),
    blocks: raw.blocks.map((block) => ({ code: block.code, message: block.message, paymentId: block.payment_id, status: block.status ?? null })),
    purchaseOrder: toPurchaseOrderChange(raw.purchase_order),
    swappedInvoicePurchaseOrder: raw.swapped_invoice_purchase_order
      ? toPurchaseOrderChange(raw.swapped_invoice_purchase_order)
      : null,
  }
}

export class HttpInvoiceMoveRepository implements InvoiceMoveRepository {
  constructor(private http: HttpClient) {}

  async options(contractId: number, fromPaymentId: number, invoiceId: number): Promise<InvoiceMovePlan[]> {
    const response = await this.http
      .url(`contracts/${contractId}/payments/${fromPaymentId}/invoices/${invoiceId}/move/options`)
      .get<{ options: RawPlan[] }>()
    return response.options.map(toPlan)
  }

  async suggestions(contractId: number, toPaymentId: number): Promise<InvoiceMovePlan[]> {
    const response = await this.http
      .url(`contracts/${contractId}/payments/${toPaymentId}/invoice_suggestions`)
      .get<{ suggestions: RawPlan[] }>()
    return response.suggestions.map(toPlan)
  }

  async move(request: InvoiceMoveRequest): Promise<void> {
    await this.http
      .url(`contracts/${request.contractId}/payments/${request.fromPaymentId}/invoices/${request.invoiceId}/move`)
      .body({ to_payment_id: request.toPaymentId, mode: request.mode })
      .post()
  }
}
